// chartLayout — pure geometry for the dashboard chart auto-tiler.
//
// Charts without an explicit anchor_cell are laid out on a 2-column grid
// below the KPI band. Placement is OCCUPANCY-AWARE: before choosing a slot
// the applier loads every chart already on the sheet (prior turns, earlier
// charts in this apply run, user-moved charts) and takes the first grid
// tile whose rectangle intersects none of them. A plain per-run counter
// restarts at 0 on every Apply click, which stacked turn-2 charts
// pixel-exact on top of turn-1 charts — the bug this module exists to fix.
//
// Everything here is pure (rect math only) so it unit-tests without an
// Office.js host; the applier owns the Excel proxy calls.

export interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export const CHART_TILE = {
  // First tile row (0-based). The premium dashboard layout reserves rows
  // 1-10 for the title band + KPI cards; charts start below them.
  originRow: 11,
  // Two chart columns side by side — the reading pattern every major BI
  // tool uses and what the dashboard skill's "2-column grid" promises.
  gridCols: 2,
  // Tile span in sheet cells: chart body 15 rows × 7 cols…
  tileRows: 15,
  tileCols: 7,
  // …separated by a 2-row / 2-col gutter so charts never touch.
  gutterRows: 2,
  gutterCols: 2,
  // 6 grid rows × 2 cols. The skill targets 3-5 charts per dashboard, so
  // 12 slots is generous headroom, not a practical limit.
  maxTiles: 12,
  // Charts sharing a gutter edge can bleed a point or two — tolerate that
  // instead of declaring a false collision.
  overlapEpsilonPt: 4,
} as const;

// rectsOverlap reports whether two rects intersect after shrinking each
// edge test by epsilon points, so mere edge contact doesn't count.
export function rectsOverlap(a: Rect, b: Rect, epsilonPt: number): boolean {
  return (
    a.left + epsilonPt < b.left + b.width &&
    b.left + epsilonPt < a.left + a.width &&
    a.top + epsilonPt < b.top + b.height &&
    b.top + epsilonPt < a.top + a.height
  );
}

// firstFreeTileIndex returns the lowest tile index whose rect intersects no
// occupied rect, or -1 when every tile is taken (caller falls back to
// count-based placement — degenerate >12-chart sheets only).
export function firstFreeTileIndex(
  tileRects: readonly Rect[],
  occupied: readonly Rect[],
  epsilonPt: number,
): number {
  for (let i = 0; i < tileRects.length; i++) {
    const tile = tileRects[i];
    if (!occupied.some((r) => rectsOverlap(tile, r, epsilonPt))) return i;
  }
  return -1;
}

// tileAnchorCell maps a tile index to its top-left cell (0-based row/col)
// on the 2-column grid.
export function tileAnchorCell(index: number): { row: number; col: number } {
  const gridRow = Math.floor(index / CHART_TILE.gridCols);
  const gridCol = index % CHART_TILE.gridCols;
  return {
    row: CHART_TILE.originRow + gridRow * (CHART_TILE.tileRows + CHART_TILE.gutterRows),
    col: gridCol * (CHART_TILE.tileCols + CHART_TILE.gutterCols),
  };
}
